import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { node } from "prop-types";
import {
  DISPLAY_CURRENCY_OPTIONS,
  formatPrice,
  getActiveRates,
  resolveDisplayCurrency,
} from "../utils/currency";
import { CurrencyContext } from "./currencyContext";
import { getMyPreferences, updateMyPreferences } from "../users/service/userApi";
import { claimRefusals } from "../utils/refusalNotice";
import { useUser } from "../users/providers/UserProvider";

const STORAGE_KEY = "displayCurrency";

/** `chosenBy` when nobody has picked a currency yet this session. */
const NO_CHOICE = Symbol("currency/no-choice");

const readStoredPreference = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return DISPLAY_CURRENCY_OPTIONS.includes(stored) ? stored : "auto";
  } catch (_) {
    return "auto";
  }
};

const storePreference = (preference) => {
  try {
    localStorage.setItem(STORAGE_KEY, preference);
  } catch (_) {
    // Private mode / quota — the account copy still holds the preference.
  }
};

// A pick whose account save failed: { u: user id, v: currency }. Without it the
// next launch adopted the account copy, still the old value, and undid the pick
// with no message (code review ERRORS-STATES-14).
const PENDING_KEY = "displayCurrencyPendingSync";

const readPending = () => {
  try {
    const p = JSON.parse(localStorage.getItem(PENDING_KEY) || "null");
    return p && DISPLAY_CURRENCY_OPTIONS.includes(p.v) ? p : null;
  } catch (_) {
    return null;
  }
};

const writePending = (value) => {
  try {
    if (value) localStorage.setItem(PENDING_KEY, JSON.stringify(value));
    else localStorage.removeItem(PENDING_KEY);
  } catch (_) {
    // Storage blocked: nothing to carry to the next launch either.
  }
};

/**
 * Holds the display-currency preference (#64) and hands every surface the
 * SAME format function, so one render pass can never mix conversion results.
 *
 * Persistence: localStorage always (works signed-out), the account when
 * signed in. On sign-in the account copy wins — it is what follows the user
 * across devices; the local copy is re-mirrored from it.
 *
 * `useCurrency` lives in ./currencyContext so that consuming a price does not
 * drag the account API into the importing module.
 */
export const CurrencyProvider = ({ children }) => {
  const { token, user } = useUser();
  const userId = user?._id ?? null;
  const [preference, setPreferenceState] = useState(readStoredPreference);
  // An explicit choice always beats an account load that is still in flight.
  // Without this, changing currency in the first moments after sign-in gets
  // silently reverted when the GET finally resolves.
  //
  // It records WHOSE choice it is, not just that one happened (#167): the
  // provider is mounted around the whole route tree (App.js:48, with
  // `<Router />` at App.js:60) and sign-out only clears state
  // (useHandleUsers.js:126-131 — `removeToken(); setToken(null);
  // setUser(null);`, no reload), so a plain boolean survived into the next
  // person's session and threw away the account preference they had just
  // fetched. Values: NO_CHOICE (nobody has picked), null (picked while signed
  // out — it belongs to whoever signs in next), or the `user._id` that picked.
  //
  // The `null` value is UNREACHABLE in the app as it stands, and the branch
  // that consumes it is defensive only. `setPreference` has exactly one caller,
  // DisplayCurrencySetting.jsx:55, mounted solely from EditUserPage.jsx:361,
  // whose route is wrapped in `<RequireAuth>` (Router.jsx:134) — and RequireAuth
  // gates on `user`, not `token` (RequireAuth.jsx:11,19), so a pick can never be
  // made with no `user._id`. Keep the branch if a signed-out currency control is
  // ever added; do not write prose that claims users hit it today.
  const chosenBy = useRef(NO_CHOICE);

  useEffect(() => {
    if (!token) return;
    // Ownership is settled here, synchronously, before the request goes out —
    // ahead of the `.then` guard below in the same effect, so a reset can
    // never land after the response it was meant to let through.
    //
    // Keyed on the user id, NOT on `token`: UserProvider bridges every
    // Supabase TOKEN_REFRESHED into a freshly minted app JWT
    // (UserProvider.jsx:175-177 -> :74-77), and the server signs it with no
    // `noTimestamp` (server/auth/providers/jwt.js:40-45), so the token STRING
    // changes ~hourly for one unchanged person. Forgetting the choice on that
    // change would re-apply the account copy — stale exactly when the PUT in
    // setPreference failed, which it does silently — and revert the user's own
    // pick an hour later. `user._id` is stable across that re-mint; the `user`
    // object identity is not (bridgeSession calls setUser with a fresh decode).
    //
    // `userId === null` here means a token without a decoded identity yet, so
    // it decides nothing and waits: this effect re-runs when the id arrives.
    if (userId !== null) {
      if (chosenBy.current === null) {
        chosenBy.current = userId;
      } else if (
        chosenBy.current !== NO_CHOICE &&
        chosenBy.current !== userId
      ) {
        chosenBy.current = NO_CHOICE;
      }
    }
    // This person's last pick never reached the account: it is still their
    // choice, so send it again rather than adopt the stale account copy.
    const pending = readPending();
    if (userId !== null && chosenBy.current === NO_CHOICE && pending?.u === String(userId)) {
      chosenBy.current = userId;
      setPreferenceState(pending.v);
      storePreference(pending.v);
      updateMyPreferences({ displayCurrency: pending.v })
        .then(() => writePending(null))
        .catch(() => {
          // Still offline: the marker stays for the next launch.
        });
      return undefined;
    }
    let cancelled = false;
    getMyPreferences()
      .then((prefs) => {
        if (cancelled || chosenBy.current !== NO_CHOICE) return;
        const account = prefs?.displayCurrency;
        if (DISPLAY_CURRENCY_OPTIONS.includes(account)) {
          setPreferenceState(account);
          storePreference(account);
        }
      })
      .catch(() => {
        // Account copy unavailable — the local preference keeps working.
      });
    return () => {
      cancelled = true;
    };
  }, [token, userId]);

  const setPreference = useCallback(
    (next) => {
      if (!DISPLAY_CURRENCY_OPTIONS.includes(next)) return;
      chosenBy.current = userId;
      setPreferenceState(next);
      storePreference(next);
      if (token) {
        // Local copy already applied; a refused sync is not news either
        // (B-31). A failed one is remembered so the next launch re-sends it.
        updateMyPreferences({ displayCurrency: next })
          .then(() => writePending(null))
          .catch((err) => {
            if (userId !== null) writePending({ u: String(userId), v: next });
            return claimRefusals(err);
          });
      }
    },
    [token, userId]
  );

  const value = useMemo(() => {
    const rates = getActiveRates();
    const displayCurrency = resolveDisplayCurrency(preference);
    // `amountCurrency` is what the figure IS — EUR for a Cardmarket estimate,
    // USD for a provider quote. Optional and defaulted inside formatPrice, so
    // every existing call site keeps rendering exactly as before and only the
    // ones holding a real currency have to pass it.
    const format = (amount, amountCurrency) =>
      formatPrice(amount, { displayCurrency, rates, amountCurrency });
    return {
      preference,
      displayCurrency,
      rates,
      options: DISPLAY_CURRENCY_OPTIONS,
      setPreference,
      format,
      formatAmount: (amount, amountCurrency) => format(amount, amountCurrency).text,
    };
  }, [preference, setPreference]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

CurrencyProvider.propTypes = {
  children: node.isRequired,
};
